import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonPage,
  useIonRouter,
} from '@ionic/react';
import {
  AmbulanceIcon,
  FirstAidIcon,
  ShieldCheckIcon,
  SyringeIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useRef } from 'react';

import { RefresherEventDetail } from '@ionic/core';
import {
  add,
  checkmark,
  checkmarkCircleSharp,
  chevronDownOutline,
  closeCircleSharp,
  helpCircle,
  removeCircleSharp,
  syncOutline,
} from 'ionicons/icons';

import { iconProps } from '../../App';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import Navigation from '../../components/UI/Navigation';
import OptionCard from '../../components/UI/OptionCard';
import PageRefresher from '../../components/UI/PageRefresher';
import Sorter from '../../components/UI/Sorter';
import Spinner from '../../components/UI/Spinner';
import { usePets } from '../../context/PetsContext';
import { useSiniestros } from '../../context/SiniestrosContext';
import { usePetActions } from '../../hooks/Pet/usePetUtils';
import { useAlert } from '../../hooks/UI/useAlert';
import { useModal } from '../../hooks/UI/useModal';
import { TipoSiniestro } from '../../types/Siniestro/types';
import { isDawerPet } from '../../utils/coverage';
import { formatDateDDMMYYYY } from '../../utils/date';
import {
  cleanDenunciaByTipo,
  siniestros_sortCriteria,
  truncateDenuncia,
} from '../../utils/siniestro';
import './SiniestrosPage.scss';

const SINIESTRO_ICON_MAP: Record<TipoSiniestro, React.ComponentType<any>> = {
  accidente: AmbulanceIcon,
  vacuna: SyringeIcon,
  enfermedad: FirstAidIcon,
};

type SiniestroStatusTone = 'pending' | 'cancelled' | 'approved' | 'rejected' | 'unknown';
type SiniestroStatusMeta = {
  label: string;
  tone: SiniestroStatusTone;
  icon: string;
};

const getSiniestroStatusMeta = (estado?: string): SiniestroStatusMeta => {
  const rawLabel = (estado ?? 'Sin estado').trim() || 'Sin estado';
  const normalized = rawLabel.toLowerCase();

  if (normalized.includes('pend') || normalized.includes('activo')) {
    return {
      label: rawLabel,
      tone: 'pending',
      icon: syncOutline,
    };
  }
  if (normalized.includes('cancel')) {
    return {
      label: rawLabel,
      tone: 'cancelled',
      icon: removeCircleSharp,
    };
  }
  if (normalized.includes('rechaz')) {
    return {
      label: rawLabel,
      tone: 'rejected',
      icon: closeCircleSharp,
    };
  }
  if (normalized.includes('liqui')) {
    return {
      label: rawLabel,
      tone: 'approved',
      icon: checkmark,
    };
  }
  if (normalized.includes('aprob')) {
    return {
      label: rawLabel,
      tone: 'approved',
      icon: checkmarkCircleSharp,
    };
  }
  if (normalized.includes('complet')) {
    return {
      label: rawLabel,
      tone: 'approved',
      icon: checkmarkCircleSharp,
    };
  }

  return { label: rawLabel, tone: 'unknown', icon: helpCircle };
};

const SiniestrosPage: React.FC = () => {
  const { push } = useIonRouter();
  const { currentPet } = usePets();
  const [activeSortKey, setActiveSortKey] = React.useState<string | undefined>(undefined);
  const [openAccordionId, setOpenAccordionId] = React.useState<string | number | null>(
    null,
  );
  const { siniestros, isValidating, setCurrentSiniestro, mutateSiniestros } =
    useSiniestros();
  const { isOpen, content, openModal, closeModal } = useModal();
  const { showAlert } = useAlert();
  const isDawer = isDawerPet(currentPet);
  const { openNuevoSiniestro } = usePetActions({
    pet: currentPet,
    openModal,
    closeModal,
    showAlert,
  });

  React.useEffect(() => {
    if (activeSortKey !== 'estado') {
      setOpenAccordionId(null);
    }
  }, [activeSortKey]);

  const toggleAccordion = React.useCallback((id: string | number) => {
    setOpenAccordionId((prev) => (prev === id ? null : id));
  }, []);

  const isRefresherRunning = useRef<boolean>(false);
  const visibleSiniestros = React.useMemo(() => {
    if (!siniestros) return siniestros;
    return isDawer
      ? siniestros.filter((siniestro) => siniestro.tipo !== 'vacuna')
      : siniestros;
  }, [isDawer, siniestros]);
  const hasSiniestros = Boolean(visibleSiniestros?.length);

  const handleSiniestroOnClick = useCallback(
    (siniestro: any) => {
      setCurrentSiniestro(siniestro);
      push('/tabs/pet/siniestro');
    },
    [setCurrentSiniestro, push],
  );

  const refresher = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      isRefresherRunning.current = true;
      await mutateSiniestros();
    } finally {
      isRefresherRunning.current = false;
      event.detail.complete();
    }
  };

  const EmptyState = () => (
    <div className="siniestros-page__empty-state">
      <div className="siniestros-page__empty-state-icon">
        <ShieldCheckIcon size={40} weight="fill" />
      </div>
      <h2 className="siniestros-page__empty-state-title">¡Todo limpio por aquí!</h2>
      <p className="siniestros-page__empty-state-description">
        {isDawer
          ? 'No hay accidentes o enfermedades registrados.'
          : 'No hay incidentes, enfermedades o vacunas registradas.'}
      </p>

      <Button onClick={openNuevoSiniestro}>Informar caso</Button>
    </div>
  );

  const isInitialLoading = isValidating && !siniestros;

  return (
    <IonPage className="siniestros-page">
      <Navigation title="Accidentes y enfermedades" />

      <IonContent className="ion-padding">
        <PageRefresher callback={refresher} />

        {(isInitialLoading ||
          (isValidating && !isRefresherRunning.current && !isOpen)) && (
          <Spinner fullscreen />
        )}

        {!isInitialLoading && !hasSiniestros && <EmptyState />}

        {hasSiniestros && (
          <>
            <Sorter
              items={visibleSiniestros ?? []}
              criteria={siniestros_sortCriteria}
              defaultKey="recent"
              storageKey="siniestros_sort"
              onSortKeyChange={setActiveSortKey}
            >
              {(sorted) =>
                sorted.map((siniestro) => {
                  const Icon =
                    SINIESTRO_ICON_MAP[siniestro.tipo as TipoSiniestro] || FirstAidIcon;
                  const statusMeta = getSiniestroStatusMeta(siniestro.estado);
                  const showEstadoAccordion = activeSortKey === 'estado';
                  const isAccordionOpen = openAccordionId === siniestro.id;

                  return (
                    <OptionCard
                      key={siniestro.id}
                      icon={<Icon size={28} {...iconProps} />}
                      title={
                        <div className="siniestros-page__title-row">
                          <span className="siniestros-page__title-text">
                            {truncateDenuncia(
                              cleanDenunciaByTipo({
                                denuncia: siniestro.denuncia_administrativa,
                                tipo: siniestro.tipo as TipoSiniestro,
                              }),
                            )}
                          </span>
                        </div>
                      }
                      description={
                        <div className="siniestros-page__meta">
                          <div className="siniestros-page__meta-row">
                            {formatDateDDMMYYYY(siniestro.fecha_carga)}
                          </div>
                          {showEstadoAccordion && (
                            <div
                              className={`siniestros-page__accordion ${
                                isAccordionOpen ? 'is-open' : ''
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="siniestros-page__accordion-toggle"
                                aria-expanded={isAccordionOpen}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAccordion(siniestro.id);
                                }}
                              >
                                <span className="siniestros-page__accordion-label">
                                  Estado
                                </span>
                                <IonIcon
                                  icon={chevronDownOutline}
                                  className={`siniestros-page__accordion-icon siniestros-page__accordion-icon--${statusMeta.tone}`}
                                />
                              </button>

                              <div className="siniestros-page__accordion-content">
                                <span
                                  className={`siniestros-page__accordion-value siniestros-page__accordion-value--${statusMeta.tone}`}
                                >
                                  <IonIcon
                                    icon={statusMeta.icon}
                                    className="siniestros-page__accordion-value-icon"
                                  />
                                  {statusMeta.label}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      }
                      onClick={() => handleSiniestroOnClick(siniestro)}
                    />
                  );
                })
              }
            </Sorter>

            <IonFab
              vertical="bottom"
              horizontal="end"
              slot="fixed"
              onClick={openNuevoSiniestro}
            >
              <IonFabButton>
                <IonIcon icon={add} size="large" />
              </IonFabButton>
            </IonFab>
          </>
        )}

        <Modal isOpen={isOpen} onClose={closeModal}>
          {content}
        </Modal>
      </IonContent>
    </IonPage>
  );
};

export default SiniestrosPage;
